/**
 * @ngdoc controllers
 * @memberof mercury.modules.reports
 * @name ReportsLabourOverviewCtrl
 * @description Overview report for Labour section, labour versus sales
 */
angular.module('mercury.modules.reports.labour')
.controller('ReportsLabourOverviewCtrl', ["$scope", "$filter", "$q", "title", "reporttime", "ReportItemCategories", "TimeClock", "chartHelpers", "userinfo", function($scope, $filter, $q, title,
    reporttime, ReportItemCategories, TimeClock, chartHelpers, userinfo) {
  title.set('Overview');

  var maxOrders = 0;
  var maxWages = 0;

  var costCentreCodeMap = {
    f: 'Food',
    b: 'Beverage'
  };

  $scope.selectedCostCentre = 'All';
  $scope.selectedSalesCategory = 'All';

  // The chart is pretty customized for this report
  $scope.chartOptions = {
    seriesType: 'bars',
    curveType: 'linear',
    series: {
      0: {
        type: 'line',
        targetAxisIndex: 1,
        color: '#9645dd'
      },
      1: {
        type: 'bar',
        targetAxisIndex: 0,
        color: '#BAD3A2'
      }
    },
    vAxes: [{
      ticks: [],
      title: 'Labour',
      gridlines: {count: 5},
      minValue: 0
    }, {
      ticks: [],
      title: 'Orders (Inc)',
      gridlines: {count: 5, color: 'transparent'},
      baselineColor: 'transparent',
      minValue: 0
    }],
    vAxis: {
      gridlines: {count: 5}
    },
    hAxis: {
      ticks: [],
      gridlines: {
        color: 'transparent'
      }
    },
    legend: {position: 'none'}
  };

  $scope.graphColumns = [
    {label: 'Period', type: 'datetime', key: 'startTime', filter: 'reporttimeformat'},
    {label: 'Orders', type: 'number', key: 'orders', filter: 'currency'},
    {label: 'Wages', type: 'number', key: 'wages', filter: 'currency'}
  ];

  /**
   * Takes the data returned from the orders, sales, and timeclock API calls and creates a single result object
   * which contains overall series data, as well as per-category (food/beverage) series data.
   *
   * @param {*} ordersData Response from PosItemCategoryOrders API call
   * @param {*} salesData Response from PosItemCategorySales API call
   * @param {*} wageData Response from TimeClocksSummary API call
   */
  function processData(ordersData, salesData, wageData) {
    // We need to assume the number of cost centres can change, so we need to make our result structure flexible
    var tableData = {};

    // Find the unique sale categories that exist in the orders and sales data
    var salesCategoryMap = {};
    ordersData.forEach(function(ordersForPeriod) {
      ordersForPeriod.payload.forEach(function(categoryOrder) {
        salesCategoryMap[categoryOrder.label] = true;
      });
    });
    salesData.forEach(function(salesForPeriod) {
      salesForPeriod.payload.forEach(function(categorySale) {
        salesCategoryMap[categorySale.label] = true;
      });
    });
    var salesCategories = Object.keys(salesCategoryMap);
    $scope.salesCategoryOptions = ['All'].concat(salesCategories.sort());

    // Find the unique cost centres that exist in the wage data.
    var costCentreMap = {};
    wageData.forEach(function(wagesForPeriod) {
      wagesForPeriod.roles.forEach(function(categoryRoles) {
        // The backend currently returns us role cost group of 'f' or 'b', we
        // need to map this to a human readable Labour Catgory which is what
        // the costCentreCodeMap is for. However we can also end up with 'null'
        // being returned by the API when the company isn't using role groups
        // that we can (currently) map to 'f' or 'b'.
        var mappedCostCentre = costCentreCodeMap[categoryRoles.roleCostGroup];
        if (mappedCostCentre) {
          costCentreMap[mappedCostCentre] = true;
        }
      });
    });
    var costCentres = Object.keys(costCentreMap);
    $scope.costCentreOptions = ['All'].concat(costCentres.sort());

    // Loop through each interval in the orders result and create an array
    // element for each cost centre and one for all
    ordersData.forEach(function(order) {
      var intervalOrders = 0;
      var intervalSales = 0;
      var intervalWages = 0;
      var intervalCostCentreOrders = {};
      var intervalCostCentreSales = {};
      var intervalCostCentreWages = {};

      salesCategories.forEach(function(saleCategory) {
        intervalCostCentreOrders[saleCategory] = 0;
        intervalCostCentreSales[saleCategory] = 0;
      });

      costCentres.forEach(function(costCentre) {
        intervalCostCentreWages[costCentre] = 0;
      });

      // Each order has a payload object which splits data by food and beverage,
      // we allocate the orders to an overall orders total, and also by category
      order.payload.forEach(function(categoryData) {
        var saleCategoryCode = categoryData.label;
        intervalOrders += categoryData.amount;
        intervalCostCentreOrders[saleCategoryCode] += categoryData.amount;
      });

      // Find the matching sale data for the time period and add the sales
      // data to the interval totals.
      var currentPeriodSalesData = salesData.find(function(sale) {
        return (new Date(sale.startTime)).getTime() === (new Date(order.startTime)).getTime();
      });

      currentPeriodSalesData.payload.forEach(function(categoryData) {
        var saleCategoryCode = categoryData.label;
        intervalSales += categoryData.invoices - categoryData.invoiceTax;
        intervalCostCentreSales[saleCategoryCode] += categoryData.invoices - categoryData.invoiceTax;
      });

      // also find the matching wage data for the time period
      var currentPeriodWageData = wageData.find(function(wageResult) {
        return (new Date(wageResult.startTime)).getTime() === (new Date(order.startTime)).getTime();
      });

      intervalWages += currentPeriodWageData.cost;

      currentPeriodWageData.roles.forEach(function(roleWages) {
        var costCentreCode = costCentreCodeMap[roleWages.roleCostGroup];
        intervalCostCentreWages[costCentreCode] += roleWages.cost;
      });

      // Push result objects for the interval to the overall and cost centre
      // result objects.
      var label = $filter('reporttimeformat')(order.startTime);
      var startTime = moment(order.startTime);

      tableData[order.startTime] = {
        startTime: startTime.toDate(),
        moment: startTime,
        label: label,
        orders: {},
        invoices: {},
        wages: {}
      };

      // Allocate the interval data for the various categories / cost centres to the
      // appropriate bucket.
      tableData[order.startTime].orders.All = intervalOrders;
      tableData[order.startTime].invoices.All = intervalSales;
      tableData[order.startTime].wages.All = intervalWages;

      salesCategories.forEach(function(category) {
        tableData[order.startTime].orders[category] = intervalCostCentreOrders[category];
        tableData[order.startTime].invoices[category] = intervalCostCentreSales[category];
      });

      costCentres.forEach(function(costCentre) {
        tableData[order.startTime].wages[costCentre] = intervalCostCentreWages[costCentre];
      });

    });

    return tableData;
  }

  $scope.loading = $q.all({
    orders: ReportItemCategories.getOrdersByInterval(reporttime.start(), reporttime.end(), reporttime.interval()),
    sales: ReportItemCategories.getSalesByInterval(reporttime.start(), reporttime.end(), reporttime.interval()),
    wages: TimeClock.getSummaryByInterval(reporttime.start(), reporttime.end(), reporttime.interval())
  });

  $scope.loading.then(function(results) {
    $scope.tableData = processData(results.orders, results.sales, results.wages);

    var axisTicks = [];
    Object.values($scope.tableData).forEach(function(interval) {
      axisTicks.push({
        v: interval.startTime,
        f: interval.label
      });
    });

    $scope.chartOptions.hAxis.ticks = axisTicks;

    $scope.updateDataSelectors();
  });

  // When switching categories or cost centers we also need to change the scale
  // of the chart, so we redefine the reportChartData and chartOptions array.
  $scope.updateDataSelectors = function() {
    reportChartData = [];
    var hasPayrollSalaryPermissions = userinfo.companyHasPermission('Payroll,Salary');
    var rows = Object.values($scope.tableData);
    for (var i = 0; i < rows.length; i++) {
      reportChartData.push({
        startTime: rows[i].startTime,
        orders: rows[i].orders[$scope.selectedSalesCategory],
        wages: hasPayrollSalaryPermissions ? rows[i].wages[$scope.selectedCostCentre] : 0
      });
    }

    minOrders = 0;
    minWages = 0;
    maxOrders = 0;
    maxWages = 0;

    reportChartData.forEach(function(interval) {
      if (interval.orders < minOrders) { minOrders = interval.orders; }
      if (interval.wages < minWages) { minWages = interval.wages; }
      if (interval.orders > maxOrders) { maxOrders = interval.orders; }
      if (interval.wages > maxWages) { maxWages = interval.wages; }
    });

    if (maxOrders === 0) { maxOrders = 1000; }
    if (maxWages === 0) { maxWages = 1000; }

    $scope.reportChartData = reportChartData;
    $scope.chartOptions.vAxes[1].minValue = minOrders;
    $scope.chartOptions.vAxes[0].minValue = minWages;
    $scope.chartOptions.vAxes[1].maxValue = maxOrders;
    $scope.chartOptions.vAxes[0].maxValue = maxWages;

    var orderTicks = chartHelpers.generateChartTicks(minOrders, maxOrders, 10, 5, 10);
    $scope.chartOptions.vAxes[1].ticks = orderTicks;
    // The number of Order axes ticks can be one more than requested (4th
    // argument to generateChartTicks) when there are negative order values
    // for a period, in this case the $0.00 axes is also added as a tick.
    // To ensure the Labour axes ticks line up with the Order axes we need
    // to pass the actual number of ticks through when generating the Labour
    // set.
    $scope.chartOptions.vAxes[0].ticks = chartHelpers.generateChartTicks(minWages, maxWages, 10, orderTicks.length, 10);
  };

  $scope.total = function(key, costCenter) {
    return Object.values($scope.tableData).reduce(function(total, row) {
      return total + row[key][costCenter];
    }, 0);
  };
}]);
